// src/oura/ui/EngineLogo.tsx
//
// The engine's own mark beside its name (YW, 27 Sep: "include the icons/logos
// of the different AIs"). Marks from LobeHub Icons, MIT licence,
// https://github.com/lobehub/lobe-icons. OpenAI is drawn in ink; Gemini and
// Google keep their own colours.
//
// Inline rather than a sprite: a sprite needs a document-level <svg> the app
// does not have. The Gemini gradients carry ids, and the same logo renders
// many times on one screen (filter, table head, drawer), so each instance
// gets its own ids from useId or every copy would paint with the first.

import { useId } from "react";
import { cn } from "../lib/cn";

export type EngineKey = "chatgpt" | "gemini" | "ai_overview";

const OPENAI =
    "M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z";

const GEMINI =
    "M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z";

/** Gemini's three colour washes over its blue base: green, red, yellow. */
const GEMINI_WASH = [
    { x1: 7, x2: 11, y1: 15.5, y2: 12, color: "#08B962", stop: 1 },
    { x1: 8, x2: 11.5, y1: 5.5, y2: 11, color: "#F94543", stop: 1 },
    { x1: 3.5, x2: 17.5, y1: 13.5, y2: 12, color: "#FABC12", stop: 0.46 },
] as const;

const GOOGLE = [
    { d: "M23 12.245c0-.905-.075-1.565-.236-2.25h-10.54v4.083h6.186c-.124 1.014-.797 2.542-2.294 3.569l-.021.136 3.332 2.53.23.022C21.779 18.417 23 15.593 23 12.245z", fill: "#4285F4" },
    { d: "M12.225 23c3.03 0 5.574-.978 7.433-2.665l-3.542-2.688c-.948.648-2.22 1.1-3.891 1.1a6.745 6.745 0 01-6.386-4.572l-.132.011-3.465 2.628-.045.124C4.043 20.531 7.835 23 12.225 23z", fill: "#34A853" },
    { d: "M5.84 14.175A6.65 6.65 0 015.463 12c0-.758.138-1.491.361-2.175l-.006-.147-3.508-2.67-.115.054A10.831 10.831 0 001 12c0 1.772.436 3.447 1.197 4.938l3.642-2.763z", fill: "#FBBC05" },
    { d: "M12.225 5.253c2.108 0 3.529.892 4.34 1.638l3.167-3.031C17.787 2.088 15.255 1 12.225 1 7.834 1 4.043 3.469 2.197 7.062l3.63 2.763a6.77 6.77 0 016.398-4.572z", fill: "#EB4335" },
] as const;

function GeminiMark() {
    // useId's output can hold characters that are awkward inside url(#...).
    const base = `gem${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
    return (
        <>
            <path d={GEMINI} fill="#3186FF" />
            {GEMINI_WASH.map((_, i) => (
                <path key={i} d={GEMINI} fill={`url(#${base}-${i})`} />
            ))}
            <defs>
                {GEMINI_WASH.map((w, i) => (
                    <linearGradient key={i} id={`${base}-${i}`} gradientUnits="userSpaceOnUse" x1={w.x1} x2={w.x2} y1={w.y1} y2={w.y2}>
                        <stop stopColor={w.color} />
                        <stop offset={w.stop} stopColor={w.color} stopOpacity={0} />
                    </linearGradient>
                ))}
            </defs>
        </>
    );
}

/**
 * The mark for ChatGPT, Gemini or Google's AI. Renders nothing for any other
 * engine (Claude, Perplexity): no mark is better than a wrong one.
 *
 * Decorative by default, since the engine's name always sits beside it; pass
 * `label` where the logo stands alone.
 */
export function EngineLogo({ engine, size = 16, label, className }: { engine: EngineKey | (string & {}); size?: number; label?: string; className?: string }) {
    if (engine !== "chatgpt" && engine !== "gemini" && engine !== "ai_overview") return null;
    const a11y = label ? { role: "img", "aria-label": label } : { "aria-hidden": true };
    return (
        <svg
            {...a11y}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            className={cn("shrink-0", engine === "chatgpt" && "text-[#0D0D0D]", className)}
        >
            {engine === "chatgpt" && <path d={OPENAI} fill="currentColor" fillRule="evenodd" />}
            {engine === "gemini" && <GeminiMark />}
            {engine === "ai_overview" && GOOGLE.map((p) => <path key={p.fill} d={p.d} fill={p.fill} />)}
        </svg>
    );
}
